
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Plus, Package, Trash2, Copy, Shield, Edit, RotateCcw, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAppCatalogSearch } from "@/hooks/useAppCatalogSearch";
import { useQuery as useConvexQuery, useMutation as useConvexMutation } from "convex/react";
import { api } from "../../api";
import { AppIcon } from "./AppIcon";

interface DeviceAppsBlacklistContentProps {
  deviceId: string;
}

export const DeviceAppsBlacklistContent = ({ deviceId }: DeviceAppsBlacklistContentProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAddAppSearch, setShowAddAppSearch] = useState(false);
  const [removedLevelApps, setRemovedLevelApps] = useState<any[]>([]);
  const { toast } = useToast();

  // Fetch device data to get the deviceConfig ID
  const deviceData = useConvexQuery(api.devices.get.details, { device: deviceId as any });
  
  // Get device app configuration including blocked apps
  const deviceAppConfig = useConvexQuery(
    api.devices.apps.getConfig,
    deviceData ? { deviceConfig: deviceData.device.deviceConfig } : "skip"
  );

  // Use the app catalog search hook
  const { data: searchResults, isLoading: isSearching } = useAppCatalogSearch(searchQuery);
  
  // Convex mutation for updating device apps
  const updateDeviceApps = useConvexMutation(api.devices.apps.update);

  // Get current blocked apps from device config
  const deviceBlockedAppIds = deviceAppConfig ? 
    Object.keys(deviceAppConfig.blockedApps).filter(appId => 
      deviceAppConfig.blockedApps[appId as any] === "device"
    ) : [];

  // Get level-specific blocked app IDs
  const levelBlockedAppIds = deviceAppConfig ?
    Object.keys(deviceAppConfig.blockedApps).filter(appId =>
      deviceAppConfig.blockedApps[appId as any] === "level"
    ) : [];

  // Combine all app IDs for fetching
  const allBlockedAppIds = [...deviceBlockedAppIds, ...levelBlockedAppIds];
  const currentBlockedApps = deviceBlockedAppIds; // For backward compatibility

  // Fetch all apps and filter to get blacklisted ones
  const allAppsPage = useConvexQuery(api.apps.list.all,
    allBlockedAppIds.length > 0 ? { paginationOpts: { cursor: null, numItems: 1000 } } : "skip"
  );

  // Filter to get only blacklisted apps
  const blacklistedApps = useMemo(() => {
    if (!allAppsPage?.page) return undefined;
    const idSet = new Set(allBlockedAppIds);
    return allAppsPage.page
      .filter(app => idSet.has(app.id))
      .map(app => ({
        id: app.id,
        name: app.name,
        bundleId: app.bundleId,
        iconUrl: app.iconUrl,
        category: app.category,
        tags: app.tags
      }));
  }, [allAppsPage, allBlockedAppIds]);

  // Helper to get app source
  const getAppSource = (appId: string) => {
    if (deviceBlockedAppIds.includes(appId)) return 'Device';
    if (levelBlockedAppIds.includes(appId)) return 'Level';
    return 'Unknown';
  };

  // Helper to check if an app was removed from level
  const isRemovedLevelApp = (appId: string) => {
    return removedLevelApps.some(app => app.id === appId);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied",
        description: "Error message copied to clipboard",
      });
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleAddToBlacklist = async (app: any) => {
    if (!deviceData?.device.deviceConfig) {
      toast({
        title: "Error",
        description: "Device configuration not found. Please try again.",
        variant: "destructive",
      });
      return;
    }

    // Only check whitelist conflict if NOT using Apple App Store mode
    const isUsingAppleAppStore = deviceAppConfig?.appFilter === true;
    
    if (!isUsingAppleAppStore) {
      // Check if app is already in whitelist
      const currentAllowedApps = Object.keys(deviceAppConfig?.allowedApps || {})
        .filter(appId => deviceAppConfig?.allowedApps[appId] === 'device');
      
      if (currentAllowedApps.includes(app.id)) {
        toast({
          title: "Cannot Add to Blacklist",
          description: `${app.appName} is currently in the whitelist. Please remove it from the whitelist first before adding to the blacklist.`,
          variant: "destructive",
        });
        return;
      }
    }

    try {
      // Get current blocked apps and add the new one
      const newBlockedApps = [...currentBlockedApps, app.id];
      
      // Preserve the appFilter setting - if it's true (Apple App Store), keep it as true
      // Otherwise process the category filters
      const currentAppFilter = deviceAppConfig?.appFilter;
      const preservedAppFilter = currentAppFilter === true 
        ? true 
        : Object.fromEntries(
            Object.entries(currentAppFilter || {})
              .filter(([_, config]) => config && typeof config === 'object' && 'tagFilter' in config)
              .map(([key, config]) => [key, (config as any).tagFilter])
          );
      
      // Include the entire app filter configuration
      const result = await updateDeviceApps({
        device: deviceId as any,
        blockedApps: newBlockedApps as any[],
        allowedApps: Object.keys(deviceAppConfig?.allowedApps || {})
          .filter(appId => deviceAppConfig?.allowedApps[appId] === 'device') as any[],
        appFilter: preservedAppFilter
      });
      
      toast({
        title: "App Blocked",
        description: `${app.appName} has been added to the device blacklist.`,
      });

      setSearchQuery("");
      setShowAddAppSearch(false);
    } catch (error) {
      const errorMessage = `Failed to add app to blacklist: ${error?.message || 'Unknown error'}`;
      
      toast({
        title: "Error",
        description: (
          <div className="flex items-center justify-between gap-2">
            <span className="flex-1">{errorMessage}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copyToClipboard(errorMessage)}
              className="h-6 w-6 p-0"
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        ),
        variant: "destructive",
      });
    }
  };

  const handleRemoveApp = async (appId: string, source: string) => {
    if (!deviceData?.device.deviceConfig) {
      toast({
        title: "Error",
        description: "Device configuration not found. Please try again.",
        variant: "destructive",
      });
      return;
    }

    const app = blacklistedApps?.find((a: any) => a.id === appId);

    try {
      // Preserve the appFilter setting
      const currentAppFilter = deviceAppConfig?.appFilter;
      const preservedAppFilter = currentAppFilter === true 
        ? true 
        : Object.fromEntries(
            Object.entries(currentAppFilter || {})
              .filter(([_, config]) => config && typeof config === 'object' && 'tagFilter' in config)
              .map(([key, config]) => [key, (config as any).tagFilter])
          );

      if (source === 'Device') {
        // Remove from device blacklist
        const newBlockedApps = currentBlockedApps.filter(id => id !== appId);
      
        await updateDeviceApps({
          device: deviceId as any,
          blockedApps: newBlockedApps as any[],
          allowedApps: Object.keys(deviceAppConfig?.allowedApps || {})
            .filter(appId => deviceAppConfig?.allowedApps[appId] === 'device') as any[],
          appFilter: preservedAppFilter
        });
      } else if (source === 'Level' && app) {
        // Add to removed level apps list for the second table
        setRemovedLevelApps(prev => [...prev, app]);

        // Allow this app at device level (add to device allowed apps)
        const currentAllowedApps = Object.keys(deviceAppConfig?.allowedApps || {})
          .filter(appId => deviceAppConfig?.allowedApps[appId] === 'device');

        await updateDeviceApps({
          device: deviceId as any,
          blockedApps: currentBlockedApps as any[],
          allowedApps: [...currentAllowedApps, appId] as any[],
          appFilter: preservedAppFilter
        });
      }
      
      toast({
        title: "App Unblocked",
        description: `${app?.name} has been removed from the blacklist.`,
      });
    } catch (error) {
      console.error('Error removing app:', error);
      toast({
        title: "Error",
        description: `Failed to remove app: ${error?.message || 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  const handleRestoreApp = async (appId: string) => {
    if (!deviceData?.device.deviceConfig) {
      toast({
        title: "Error",
        description: "Device configuration not found.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Remove from allowed apps to restore level blocking
      const currentAllowedApps = Object.keys(deviceAppConfig?.allowedApps || {})
        .filter(id => deviceAppConfig?.allowedApps[id] === 'device' && id !== appId);

      const currentAppFilter = deviceAppConfig?.appFilter;
      const preservedAppFilter = currentAppFilter === true
        ? true
        : Object.fromEntries(
            Object.entries(currentAppFilter || {})
              .filter(([_, config]) => config && typeof config === 'object' && 'tagFilter' in config)
              .map(([key, config]) => [key, (config as any).tagFilter])
          );

      await updateDeviceApps({
        device: deviceId as any,
        blockedApps: currentBlockedApps as any[],
        allowedApps: currentAllowedApps as any[],
        appFilter: preservedAppFilter
      });

      // Remove from removed apps list
      setRemovedLevelApps(prev => prev.filter(app => app.id !== appId));

      const app = removedLevelApps.find(a => a.id === appId);
      toast({
        title: "App Restored",
        description: `${app?.name} has been restored to the blacklist.`,
      });
    } catch (error) {
      console.error('Error restoring app:', error);
      toast({
        title: "Error",
        description: `Failed to restore app: ${error?.message || 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  const isLoading = deviceData === undefined || deviceAppConfig === undefined || (allBlockedAppIds.length > 0 && blacklistedApps === undefined);

  // Create combined list of apps with source information
  const combinedApps = blacklistedApps
    ? blacklistedApps
        .filter((app: any) => !isRemovedLevelApp(app.id))
        .map((app: any) => ({
          ...app,
          source: getAppSource(app.id)
        }))
    : [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">App Blacklist</h3>
          <p className="text-sm text-gray-600">
            Apps blocked for this device from both device and level settings
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddAppSearch(!showAddAppSearch)}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add App
          </Button>
          {isEditMode ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditMode(false)}
            >
              Done
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditMode(true)}
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Add App Search */}
      {showAddAppSearch && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Add Apps to Blacklist</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search for apps to block..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {searchQuery && (
              <div className="space-y-2">
                {isSearching ? (
                  <div className="text-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </div>
                ) : searchResults && searchResults.length > 0 ? (
                  <>
                    <h4 className="font-medium text-sm">Search Results</h4>
                    {searchResults
                      .filter(app => !allBlockedAppIds.includes(app.id))
                      .map((app) => (
                      <div key={app.bundleId} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <AppIcon app_icon_url={app.iconUrl} app_name={app.appName} />
                          <div>
                            <p className="font-medium text-sm">{app.appName}</p>
                            <p className="text-xs text-gray-500">{app.bundleId}</p>
                          </div>
                        </div>
                        <Button size="sm" onClick={() => handleAddToBlacklist(app)}>
                          <Plus className="w-4 h-4 mr-1" />
                          Block
                        </Button>
                      </div>
                    ))}
                    {searchResults.filter(app => !allBlockedAppIds.includes(app.id)).length === 0 && (
                      <p className="text-sm text-gray-500">All search results are already blacklisted.</p>
                    )}
                  </>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">No apps found for "{searchQuery}"</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Blacklist Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Loading app blacklist...</span>
            </div>
          ) : combinedApps.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-gray-400 mb-4 mx-auto" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Apps Blacklisted</h3>
              <p className="text-gray-600 text-center max-w-md mx-auto">
                No apps are currently blacklisted for this device. Click "Add App" to search and add apps to block.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>App</TableHead>
                  <TableHead>Source</TableHead>
                  {isEditMode && <TableHead className="w-[100px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {combinedApps.map((app: any) => (
                  <TableRow key={app.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <AppIcon app_icon_url={app.iconUrl} app_name={app.name} />
                        <div>
                          <p className="font-medium text-sm">{app.name}</p>
                          <p className="text-xs text-gray-500">{app.bundleId}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={app.source === 'Device' ? 'destructive' : 'secondary'}>
                        {app.source}
                      </Badge>
                    </TableCell>
                    {isEditMode && (
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveApp(app.id, app.source)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Removed Level Apps Table */}
      {removedLevelApps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Apps that were on the blacklist by level</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>App</TableHead>
                  <TableHead>Source</TableHead>
                  {isEditMode && <TableHead className="w-[100px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {removedLevelApps.map((app: any) => (
                  <TableRow key={app.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <AppIcon app_icon_url={app.iconUrl} app_name={app.name} />
                        <div>
                          <p className="font-medium text-sm">{app.name}</p>
                          <p className="text-xs text-gray-500">{app.bundleId}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">Level</Badge>
                    </TableCell>
                    {isEditMode && (
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestoreApp(app.id)}
                          className="text-green-600 hover:text-green-700"
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Restore
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

